import { Button, Card, FormControl, InputLabel, makeStyles, MenuItem, Select, TextField } from '@material-ui/core';
import { ColDef, XGrid, SortModelParams } from '@material-ui/x-grid';
import React, { useCallback, useEffect, useState } from 'react';
import { Role, useAuthentication } from '../../contexts/Authentication';
import Fetcher, { URLBuild } from '../../services/fetcher';
import settings from '../../settings';
import UserModal from '../UserModal';
import UserCreate from '../UserCreate';

export interface UserModel {
  id: number
  slug: string | null
  username: string | null
  legacyExpire: string | null
  email: string
  emailActivated: boolean
  roles: string[]
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column'
  },
  grid: {
    display: 'flex',
    flex: 1
  },
  search: {
    display: 'flex',
    flex: 1
  },
  addUser: {
    display: 'flex',
    marginLeft: theme.spacing(2)
  },
  card: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  addRoles: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    marginBottom: 10
  },
  selectRole: {
    flex: 1,
    marginLeft: 16,
  }
}));

const filterRoles = [
  Role.Lecteur,
  Role.Abonne,
  Role.AbonneDecouverte,
  Role.AbonneLegacy,
  Role.AbonneEtudiant,
  Role.AbonneClassique,
  Role.AbonnePremium,
  Role.Administrateur,
  Role.Chroniqueur,
  Role.Communaute,
  Role.Editorial1,
  Role.Contributeur,
  Role.Editorial2,
  Role.Redacteur,
  Role.RedacteurChef,
  Role.RedacteurJunior
];

const UsersTable = () => {
  const classes = useStyles();

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(25);
  const [count, setCount] = useState(0);
  const [filter, setFilter] = useState('');
  const [data, setData] = useState<UserModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [sort, setSort] = useState({field: 'id', order: 'desc'});
  const [role, setRole] = useState("all");
  
  const [modifiying, setModifying] = useState<boolean>(false);
  const [selected, setSelected] = useState<number | undefined>(0);
  const [toggleUserModal, setToggleUserModal] = useState<boolean>(false);

  const auth = useAuthentication();

  const getData = useCallback(
    () => {
      setLoading(true);
      Fetcher.get(URLBuild(settings.OpenIdProvider.users,
        {
          sort_field: sort.field,
          sort_order: sort.order,
          start: (page - 1) * rows,
          end: page * rows,
          filter,
          role
        }))
        .then((res) => {
          setData(res.users);
          setCount(res.count);
          setLoading(false);
        })
        .catch(() => auth.logout());
    },
    [page, rows, auth, filter, role, sort],
  )

  useEffect(() => {
    getData();
  }, [page, rows, getData]);

  const selectUser = (i: number) => {
    setSelected(i);
    setModifying(true);
  }

  const onRequestClose = (update: boolean) => {
    if (update)
      getData();
    setModifying(false);
    setToggleUserModal(false);
  }

  const onSortModelChange = (params: SortModelParams) => {
    const order = params.sortModel.length > 0 ? (params.sortModel[0].sort ?? 'desc') : 'desc';
    const field = params.sortModel.length > 0 ? (params.sortModel[0].field ?? 'id') : 'id';

    setSort((prevSort) => {
      if (prevSort.field !== field || prevSort.order !== order)
        return ({field, order});
      return prevSort; 
    });
  }

  return (
    <div className={classes.container}>
      <Card className={classes.card}>
        <TextField
          className={classes.search}
          variant="outlined"
          label="Recherche par email / username"
          onChange={(e) => setFilter(e.target.value)}
        />
        <FormControl variant="outlined" className={classes.selectRole}>
          <InputLabel id="userRoleLabel">Rôle</InputLabel>
          <Select
            value={role}
            labelId="userRoleLabel"
            onChange={e => setRole(e.target.value as string)}
            label="Rôle"
            id="userRoleSelect"
          >
            <MenuItem key={0} value={"all"}>Tous les rôles</MenuItem>
            { filterRoles.map((r, i) =>
              <MenuItem key={i + 1} value={Role[r]}>{Role[r]}</MenuItem>
            )}
          </Select>
        </FormControl>
        <Button
          className={classes.addUser}
          onClick={() => setToggleUserModal(true)}
          variant="outlined"
        >
          Créer un nouvel utilisateur
        </Button>
      </Card>
      <div className={classes.grid}>
        <XGrid
          columns={[
            { field: 'id', headerName: 'ID', sortable: true },
            { field: 'username', headerName: 'Username', width: 300, sortable: true },
            { field: 'email', headerName: 'Adresse e-mail', width: 300, sortable: true },
            { field: 'emailActivated', headerName: 'Statut', width: 150, sortable: false },
            { field: 'roles', headerName: 'Roles', width: 300, sortable: false },
            { field: 'slug', headerName: 'Slug', width: 150, sortable: false },
          ] as ColDef[]}

          rows={data.map(d => ({ ...d, username: d.username ?? '-', emailActivated: d.emailActivated ? 'Validé' : 'En attente', roles: d.roles.join(' - ') }))}

          checkboxSelection={false}
          disableSelectionOnClick={true}
          disableMultipleSelection={true}
          pageSize={rows}
          paginationMode="server"
          sortingMode="server"
          onSortModelChange={onSortModelChange}
          pagination={true}
          rowsPerPageOptions={[25, 100, 1000]}
          onPageChange={(param) => setPage(param.page)}
          onPageSizeChange={(param) => setRows(param.pageSize)}
          onRowClick={(param) => selectUser(param.rowIndex)}
          rowCount={count}
          loading={loading}
        />
        { modifiying &&
          <UserModal
            open={modifiying}
            onClose={() => setModifying(false)}
            onRequestClose={onRequestClose}
            user={data[selected ?? 0]}
            updatable={true}
          />
        }
        { toggleUserModal &&
          <UserCreate
            open={toggleUserModal}
            onClose={() => setToggleUserModal(false)}
            onRequestClose={onRequestClose}
          />
        }
      </div>
    </div>
  )
}

export default UsersTable;